import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button, InlineLoading, ProgressBar, SkeletonText, Tile } from '@carbon/react';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { useBilling, useOpenBillingPortal, useStartCheckout } from 'src/hooks/useBilling';
import { useInvitations } from 'src/hooks/useInvitations';
import { useOrgMembers } from 'src/hooks/useOrgMembers';

type MeterData = { key: string; used: number; limit: number | null };

function Meter({ meter }: { meter: MeterData }) {
  const { t } = useTranslation();
  const label = t(`billing.resource.${meter.key}`);
  if (meter.limit === null) {
    return (
      <div className="billing-meter" data-meter={meter.key}>
        <strong>{label}</strong> {meter.used} · {t('billing.unlimited')}
      </div>
    );
  }
  const ratio = meter.used / meter.limit;
  const level = ratio >= 1 ? 'error' : ratio >= 0.8 ? 'warning' : 'ok';
  return (
    <div className={`billing-meter billing-meter--${level}`} data-meter={meter.key}>
      <ProgressBar
        label={label}
        helperText={`${meter.used} / ${meter.limit}`}
        value={Math.min(meter.used, meter.limit)}
        max={meter.limit}
        status={level === 'error' ? 'error' : 'active'}
      />
    </div>
  );
}

const ACTIVATION_TIMEOUT_MS = 30_000;

const OrgBilling = React.memo(() => {
  const { t, i18n } = useTranslation();
  const { isOwner } = useAuthPermissions();
  const { activeOrganizationId } = useAuthSession();
  const [params, setParams] = useSearchParams();
  const returningFromCheckout = params.has('checkout');
  const { data, isLoading } = useBilling({ pollWhileFree: returningFromCheckout });
  const members = useOrgMembers(activeOrganizationId ?? null);
  const invitations = useInvitations(activeOrganizationId ?? null);
  const checkout = useStartCheckout();
  const portal = useOpenBillingPortal();
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (!returningFromCheckout) return undefined;
    const timer = setTimeout(() => setSlow(true), ACTIVATION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [returningFromCheckout]);

  useEffect(() => {
    if (returningFromCheckout && data?.plan === 'pro') {
      const next = new URLSearchParams(params);
      next.delete('checkout');
      setParams(next, { replace: true });
    }
  }, [returningFromCheckout, data?.plan, params, setParams]);

  if (isLoading || !data) return <SkeletonText paragraph />;
  if (!data.enabled) return <Navigate to="/settings/organization" replace />;

  const date = data.currentPeriodEnd ? new Date(data.currentPeriodEnd).toLocaleDateString(i18n.language) : '';
  let subtitle: string | null = null;
  if (data.status === 'trialing') subtitle = t('billing.trialEnds', { date });
  else if (data.cancelAtPeriodEnd) subtitle = t('billing.cancels', { date });
  else if (data.plan === 'pro') subtitle = t('billing.renews', { date });

  const pendingInvites = (invitations.data ?? []).filter((invite) => invite.status === 'pending').length;
  const meters: MeterData[] = [
    { key: 'restaurant', used: data.usage.restaurants, limit: data.limits.restaurants ?? null },
    { key: 'branch', used: data.usage.branches, limit: data.limits.branches ?? null },
    { key: 'member', used: (members.data?.length ?? 0) + pendingInvites, limit: data.limits.members ?? null },
    { key: 'menuItem', used: data.usage.menuItems, limit: data.limits.menuItems ?? null },
    { key: 'ordersThisMonth', used: data.usage.ordersThisMonth, limit: data.limits.ordersPerMonth ?? null },
  ];

  let action: React.ReactNode;
  if (!isOwner) {
    action = <p>{t('billing.askOwner')}</p>;
  } else if (returningFromCheckout) {
    // Waiting for Polar's webhook: a second Upgrade here would start a second subscription.
    action = null;
  } else if (data.plan === 'free') {
    action = (
      <Button onClick={() => checkout.mutate()} disabled={checkout.isLoading}>
        {t('billing.upgradeToPro')}
      </Button>
    );
  } else {
    action = (
      <Button kind="tertiary" onClick={() => portal.mutate()} disabled={portal.isLoading}>
        {t('billing.manage')}
      </Button>
    );
  }

  return (
    <div className="billing-page">
      <h2>{t('billing.title')}</h2>
      {returningFromCheckout && data.plan !== 'pro' && (
        <div className="billing-page__activating">
          <InlineLoading description={t('billing.activating')} />
          {slow && (
            <p>
              {t('billing.activatingSlow')}{' '}
              <Button kind="ghost" size="sm" onClick={() => window.location.reload()}>
                {t('billing.refresh')}
              </Button>
            </p>
          )}
        </div>
      )}
      <Tile className="billing-page__plan">
        <h3>{t(`billing.plan.${data.plan}`)}</h3>
        {subtitle && <p>{subtitle}</p>}
        {action}
      </Tile>
      <h3>{t('billing.usage')}</h3>
      {meters.map((meter) => (
        <Meter key={meter.key} meter={meter} />
      ))}
    </div>
  );
});

export default OrgBilling;
